import { NextPageLayoutService } from './layout';
import { NextPageOptionsModel } from './models/nextpage-options';
import { NextPagePropertiesModel } from './models/nextpage-properties';
import { BlurAria } from './interfaces/iblur-aria';
import { BlurAttribute } from './interfaces/iblur-attribute';
import { BlurClass } from './interfaces/iblur-class';
import { INextpageService } from './interfaces/inextpage-service';
import { Search } from './components/search';
import { Alert } from './components/alert';
import { Drawers } from './components/drawers';
import { Dropdown } from './components/dropdown';
import { Tabbed } from './components/tabbed';
import { Tables } from './components/tables';
import { Forms } from './components/forms';
import { SliderCards } from './components/slider_cards';
import { Slider } from './components/slider';
import { NavMenu } from './components/nav_menu';
import { NavBuilder } from './components/nav_builder';
import { NavigationItem, RouteObject } from './interfaces/inavigation-item';

export class UI implements INextpageService{
    protected domAutoInit = false;

    isInitialized = false;
    layout: NextPageLayoutService;

    options: NextPageOptionsModel;
    properties: NextPagePropertiesModel = new NextPagePropertiesModel();
    events: Record<string, Function>;
    components: Record<string, Function>;
    resizeFunctions: Record<string, Function>;

    constructor(
        options: NextPageOptionsModel = new NextPageOptionsModel(), 
        initComponents = true
        ) {
            this.options = options;
            this.components = {};
            this.events = {};
            this.resizeFunctions = {};

            this.registerEvent("bodyClick", () => {
                this.blurPromise(null);
            });

            document.body.addEventListener("click", (e: Event) => this.events.bodyClick(e));

            this.properties.isMobile = window.innerWidth < this.options.mobileThreshold;
            this.properties.isTablet = window.innerWidth < this.options.tabletThreshold && window.innerWidth >= this.options.mobileThreshold;

            this.layout = new NextPageLayoutService(this, this.properties);

            const mains = document.querySelectorAll('main.container');
            if (mains.length != 0) {
                this.registerResizeFunction('layoutMorphs', this.layout.layoutMorphs);
            }
            
            new Search(this);
            new NavMenu(this, new NavBuilder(this));
            new Alert(this);
            new Drawers(this);
            new Dropdown(this);
            new Tables(this);
            new Tabbed(this);
            new Forms(this);
            new SliderCards(this);
            new Slider(this);
            if (initComponents) {
                this.components.globalSearch();
                this.components.globalSiteSpecificSearch();
        
                this.components.globalTopMenu();
                this.components.globalSiteSpecificNavigation();
                this.components.detailPageNavigation();
        
                this.components.alert();
                this.components.drawers('.drawers');
                this.components.dropdown('.dropdown');
                this.components.table('table');
                this.components.tabbed('div.tabbed');
                this.components.formSelect('form.branded select:not(.no-brand), select.branded');
                this.components.cardSlider('.slider.cards');
                this.components.slider('.slider:not(.cards)');

                const mains = document.querySelectorAll('main.container');
                if (mains.length != 0) {
                    this.components.layoutMorphs(this.properties);
                }
                this.baseInitialize();
            }

            window.onresize = (): void => {
                this.properties.resizing = true;
                this.properties.lastResize = new Date().getTime();
            }
            
            window.setInterval(this.resizeCheck, this.options.eventResizeThreshold);
    }

    protected listObjectProperties(obj: string): Array<string> {
        const objPropertyList: Array<string> = [];
        const realObj = obj === 'properties' ? this.properties : this[obj];
        for (const objPropertyName in realObj) {
            if (realObj.hasOwnProperty(objPropertyName)) {
                objPropertyList[objPropertyList.length] = objPropertyName;
            }
        }
        return objPropertyList;
    }

    /*
        If the width of the layout has changed this function will loop through the array of
        resizeFunctions and call each of them. Any components whose size may change after the
        window is resized will push a function to call in this array.

        For multiple components of the same type (e.g. sliders) only one resize function will
        be added to this array. This is because that function will apply to all components of
        the same type.
    */
    protected doResizeActions(): void {
        this.properties.resizing = false;
        const l = this.properties.resizeKeys.length;
        const mainWidth = window.innerWidth;
        let i;
        if (mainWidth === this.properties.lastLayoutWidth) {
            return;
        }
        this.properties.lastLayoutWidth = mainWidth;
        const mobileLayout = mainWidth < this.options.mobileThreshold;
        const tabletLayout = !mobileLayout && mainWidth < this.options.tabletThreshold;
        const sameLayout = this.properties.isMobile === mobileLayout || this.properties.isTablet === tabletLayout;
        this.properties.isMobile = mobileLayout;
        this.properties.isTablet = tabletLayout;
        for (i = 0; i < l; i = i + 1) {
            this.resizeFunctions[this.properties.resizeKeys[i]](sameLayout);
        }
    }

    bodyClick = (): void => {
        this.blurPromise(null);
    }

    baseInitialize(): void {
        if (this.isInitialized === true) {
            return;
        }
        this.isInitialized = true;
        document.title = this.options.sitename;
        this.properties.lastLayoutWidth = window.innerWidth;
        document.body.onclick = (): void => {
            this.blurPromise(null);
        };

        // Check layout morphs initially
        const mains = document.querySelectorAll('main.container');
        if (mains.length != 0) {
            this.components.layoutMorphs(this.properties);
        }

        // On resize make sure the resizing flag is true and set the lastResize timestamp.
        window.onresize = (): void => {
            this.properties.resizing = true;
            this.properties.lastResize = new Date().getTime();
        };

        // Call the resizeCheck at every _resizeThreshold interval.
        window.setInterval(() => { this.resizeCheck(); }, this.options.eventResizeThreshold);
    }

    setOptions = (newOptions: NextPageOptionsModel): void => {
        this.options = newOptions;
    }

    setOption = (optionKey: string, optionValue: string | number | Array<NavigationItem | RouteObject>): void => {
        this.options[optionKey] = optionValue;
    }

    setNavigation = (navArray: Array<any>): void => {
        if (navArray.length < 3) {
            console.log("You must have at least three nav objects in the `NextPage.options.navigationItems` or `NextPage.options.navigationRoutes` array.")
            return null;
        }

        if (navArray[0].hasOwnProperty("route")) {
            this.options["navigationRoutes"] = navArray;
        }
        else {
            this.options["navigationItems"] = navArray;
        }
    }

    getProperty = <T>(propertyName: string): T => {
        if (!this.properties.hasOwnProperty(propertyName)) {
            return undefined;
        }
        return this.properties[propertyName];
    }

    getComponentSelector = (selector: string | Element | NodeListOf<Element> | Array<Element>): Array<Element> => {
        if (typeof(selector) === "string") {
            return Array.from(document.querySelectorAll(selector));
        }
        if (selector instanceof Element) {
            return [selector];
        }
        if (Array.isArray(selector)) {
            return selector;
        }
        return Array.from(selector);
    }

    listProperties = (): Array<string> => {
        return this.listObjectProperties('properties');
    }

    listEvents = (): Array<string> => {
        return this.listObjectProperties('events');
    }

    listComponents = (): Array<string> => {
        return this.listObjectProperties('components');
    }

    listResizeFunctions = (): Array<string> => {
        return this.listObjectProperties('resizeFunctions');
    }

    registerEvent = (eventKey: string, eventFunction: Function): void => {
        this.events[eventKey] = eventFunction;
    }

    registerResizeFunction = (resizeKey: string, resizeFunction: Function): void => {
        this.properties.resizeKeys.push(resizeKey);
        this.resizeFunctions[resizeKey] = resizeFunction;
    }

    async blurPromise(obj: Element): Promise<Element> {
        return new Promise((resolve, reject) => {
            if (this.properties.blurAria.length === 0 && this.properties.blurAttributes.length === 0 && this.properties.blurClasses.length === 0) {
                resolve(obj);
                return;
            }
            
            let shouldReject = false;
            let rejectReason = "";
            try {
                this.properties.blurAria.forEach((blur: BlurAria) => {
                    this.toggleAriaState(blur.selector, blur.class, blur.active);
                });
            } catch (err) {
                shouldReject = true;
                rejectReason = `Could not apply blurAria:\r\n${err}`;
            }
            try {
                this.properties.blurAttributes.forEach((blur: BlurAttribute) => {
                    blur.selector.setAttribute(blur.name, blur.value);
                });
            } catch (err) {
                shouldReject = true;
                rejectReason = `Could not apply blurAttributes:\r\n${err}`;
            }
            try {
                this.properties.blurClasses.forEach((blur: BlurClass) => {
                    blur.selector.classList.toggle(blur.class, blur.active);
                });
            } catch (err) {
                shouldReject = true;
                rejectReason = `Could not apply blurClasses:\r\n${err}`;
            }
            
            this.properties.blurAria = [];
            this.properties.blurAttributes = [];
            this.properties.blurClasses = [];

            if (shouldReject === false) {
                resolve(obj);
                return;
            }

            reject(rejectReason);
        });
    }

    /*
        This performs a quick check to determine if the window is resizing. If the resizing
        variable is true and the last resize event occurred after the threshold of time set
        using the _resizeThreshold this continues to actually perform any resize events.

        Using this technique avoids calling large amounts of code at every resize event that
        may cause the browser to re-paint the DOM leading to browser screen lagging or many
        processor-intensive operations.
    */
    resizeCheck = (): void => {
        if (this.properties.resizing === false) {
            return;
        }
        const n = new Date().getTime();
        const threshold = this.properties.lastResize + this.options.eventResizeThreshold;
        if (n < threshold) {
            return;
        }
        this.doResizeActions();
    }

    toggleAriaState = function(obj: Element, ariaProperty: string, state: boolean): void {
        obj.setAttribute(`aria-${ariaProperty}`, state.toString());
        obj.classList.toggle(`aria-${ariaProperty}`, state);
    }

    mapAriaState = function(obj: Element, ariaProperty: string): void {
        const currentState = obj.getAttribute(`aria-${ariaProperty}`);
        if (currentState === undefined || currentState === null) {
            obj.classList.toggle(`aria-${ariaProperty}`, false);
            return;
        }
        if (currentState.toLowerCase() === "true") {
            obj.classList.toggle(`aria-${ariaProperty}`, true);
            return;
        }
        
        obj.classList.toggle(`aria-${ariaProperty}`, false);
    }

    numberFromData = (data: number | string): number => {
        const numberData = Number(data);
        if (data === undefined || data === "" || isNaN(numberData)) {
            return -1;
        }
        return numberData;
    }

    // Get the value of a CSS variable defined on an element
    getCssVariable = function(element: Element, key: string): string {
        return getComputedStyle(element).getPropertyValue(key);
    };

    // Set the value of a CSS variable defined on an element
    setCssVariable = function(element: HTMLElement, key: string, value: string): void {
        element.style.setProperty(key, value);
    };

    // Transform a CSS value into a usable dimension in JS (e.g. pixels or percentage)
    getCssDimension = function(dimensionData: number | string): number {
        if (dimensionData === undefined) {
            return -1;
        }
        if (typeof(dimensionData) === "string") {
            // First look for pixel-based width
            if (dimensionData.endsWith("px")) {
                return this.numberFromData(dimensionData.slice(0, -2));
            }
            // Then look for percentage-based width, return a fraction to signify percentage
            if (dimensionData.endsWith("%")) {
                return this.numberFromData(dimensionData.slice(0, -1)) / 100;
            }
        }
        // Finally just try and see if a number was provided
        return this.numberFromData(dimensionData);
    };
}